import type { HTMLAttributes } from 'react';
import { memo, useMemo } from 'react';
import { BigQueryIcon } from '@gonfalon/icons/BigQueryIcon';
import { ClickHouseIcon } from '@gonfalon/icons/ClickHouseIcon';
import { DatabricksIcon } from '@gonfalon/icons/DatabricksIcon';
import { DataDogLogoIcon } from '@gonfalon/icons/DataDogLogoIcon';
import { InvertableSentryIcon } from '@gonfalon/icons/InvertableSentryIcon';
import { InvertableSnowPlowIcon } from '@gonfalon/icons/InvertableSnowPlowIcon';
import { LaunchDarklyLogoIcon } from '@gonfalon/icons/LaunchDarklyLogoIcon';
import { OpenTelemetryIcon } from '@gonfalon/icons/OpenTelemetryIcon';
import { RedshiftIcon } from '@gonfalon/icons/RedshiftIcon';
import { SnowflakeIcon } from '@gonfalon/icons/SnowflakeIcon';
import { capitalize } from '@gonfalon/strings/capitalize';
import { Tag, TagGroup, TagList, Tooltip, TooltipTrigger } from '@launchpad-ui/components';
import clsx from 'clsx';

import styles from './source.module.css';

export interface Props extends HTMLAttributes<HTMLSpanElement> {
  sourceKey: string;
  sourceName?: string;
  integrationKey?: string;
  variant?: 'normal' | 'small' | 'plain';
  popoverContent?: React.ReactNode;
}

export const Source = memo(
  ({ sourceKey, sourceName, integrationKey, popoverContent, variant = 'normal', className, ...rest }: Props) => {
    // if integrationKey is provided, use it to determine the icon and associated class
    const iconKey = integrationKey ?? sourceKey;
    const sourceClass = useMemo(() => iconClass(iconKey), [iconKey]);
    const classes = useMemo(
      () => clsx(styles.source, sourceClass, className, styles[variant]),
      [className, sourceClass, variant],
    );

    return (
      <MaybePopover popoverContent={popoverContent}>
        <span className={classes} {...rest}>
          {renderIcon(iconKey)} <span className={styles.nameText}>{sourceName ?? getSourceNameFromKey(iconKey)}</span>
        </span>
      </MaybePopover>
    );
  },
);

function MaybePopover({ popoverContent, children }: { popoverContent?: React.ReactNode; children: React.ReactNode }) {
  if (!popoverContent) {
    return children;
  }

  return (
    <TagGroup>
      <TagList>
        <TooltipTrigger>
          <Tag className={styles.sourceTagButton}>{children}</Tag>
          <Tooltip variant="popover">{popoverContent}</Tooltip>
        </TooltipTrigger>
      </TagList>
    </TagGroup>
  );
}

/**
 * Render Icon
 *
 * Renders an icon via source indicator
 *
 * Missing:
 * 1. gamma
 * 2. meijer
 * 3. wheniwork
 */
function renderIcon(source: string) {
  switch (source) {
    case 'browser-sdk':
    case 'backend-sdk':
    case 'mobile-sdk':
    case 'edge-sdk':
      return <LaunchDarklyLogoIcon />;
    case 'datadog':
      return <DataDogLogoIcon />;
    case 'sentry':
      return <InvertableSentryIcon />;
    case 'snowflake':
    case 'snowflake-experimentation':
      return <SnowflakeIcon />;
    case 'databricks':
    case 'databricks-experimentation':
      return <DatabricksIcon />;
    case 'redshift':
    case 'redshift-experimentation':
      return <RedshiftIcon />;
    case 'bigquery':
    case 'bigquery-experimentation':
      return <BigQueryIcon />;
    case 'clickhouse':
    case 'clickhouse-experimentation':
      return <ClickHouseIcon />;
    case 'snowplow':
      return <InvertableSnowPlowIcon />;
    case 'otel-import':
    case 'OpenTelemetry':
      return <OpenTelemetryIcon />;
    default:
      return;
  }
}

/**
 * Icon Class
 *
 * Returns a class name for icons needing specific
 * color adjustments to accomodate light/dark mode
 *
 * Missing:
 * 1. gamma
 * 2. meijer
 * 3. wheniwork
 */
function iconClass(sourceKey: string) {
  switch (sourceKey) {
    case 'datadog':
    case 'snowflake':
    case 'snowflake-experimentation':
    case 'databricks':
    case 'databricks-experimentation':
    case 'redshift':
    case 'redshift-experimentation':
    case 'otel-import':
    case 'OpenTelemetry':
      return '';
    case 'bigquery':
    case 'bigquery-experimentation':
    case 'clickhouse':
    case 'clickhouse-experimentation':
      return '';
    case 'snowplow':
      return styles.snowplowIcon;
    case 'sentry':
      return styles.sentryIcon;
    default:
      return styles.ldIcon;
  }
}

/**
 * Render Source
 *
 * Snowflake Experimentation sources need a haircut. Others
 * may some day too
 */
function getSourceNameFromKey(source: string) {
  switch (source) {
    case 'snowflake':
    case 'snowflake-experimentation':
      return 'Snowflake';
    case 'databricks':
    case 'databricks-experimentation':
      return 'Databricks';
    case 'redshift':
    case 'redshift-experimentation':
      return 'Redshift';
    case 'bigquery':
    case 'bigquery-experimentation':
      return 'BigQuery';
    case 'clickhouse':
    case 'clickhouse-experimentation':
      return 'ClickHouse';
    case 'otel-import':
    case 'OpenTelemetry':
      return 'OpenTelemetry';
    case 'datadog':
    case 'snowplow':
    case 'sentry':
      return capitalize(source);
    default:
      return source;
  }
}
